import * as React from "react";
import { GridRowClassNameParams } from "@mui/x-data-grid";
import LinearProgress from "@mui/material/LinearProgress";
import { Box, Grid, useTheme, useMediaQuery } from "@mui/material";
import NoResultsOverlay from "../../components/NoResultOverlay";
import { useTitle } from "../../hooks/useTitle";
import { useGetBalanceHistoriesQuery } from "../../services/finance.service";
import { BalanceHistory } from "../../types/finance";
import TopupModalForm from "./TopupModalForm";
import { CardBase, GlassDataGrid } from "../../components/global";
import FinancesTableToolbar from "../../components/finances/FinancesTableToolbar";
import BalanceCard from "../../components/finances/BalanceCard";
import { balanceHistoryColumns } from "./columns/balanceHistoryColumns";

const FinancesPage = () => {
  useTitle("Finances");
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [topupFormModal, setTopupFormModal] = React.useState<{
    open: boolean;
  }>({
    open: false,
  });

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(100);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );

  const { data, isLoading, isFetching } = useGetBalanceHistoriesQuery({
    page,
    pageSize,
    load: "referenceModel",
    search: searchQuery,
  });
  const rowCount = React.useMemo(() => data?.meta.total, [data]);

  const rows: BalanceHistory[] | undefined = React.useMemo(
    () => data?.data,
    [data]
  );

  const getRowClassName = (params: GridRowClassNameParams) => {
    return !!params.row.deleted_at ? "deleted" : "";
  };

  const handleSearch = React.useCallback((query?: string) => {
    setSearchQuery(query);
  }, []);

  const CustomToolbar = React.useMemo(
    () => () => <FinancesTableToolbar onSearch={handleSearch} />,
    [handleSearch]
  );

  const columns = React.useMemo(
    () => balanceHistoryColumns.map((col) => {
      if (!isMobile || !col.minWidth) return col;

      // More aggressive minWidth reduction for mobile,
      // but keep comment column wider for readability
      let newMinWidth = col.minWidth * 0.5;

      if (col.field === "created_at") {
        newMinWidth = Math.max(newMinWidth, 140);
      } else if (col.field === "tx_notes") {
        newMinWidth = Math.max(newMinWidth, 220);
      } else {
        newMinWidth = Math.max(newMinWidth, 90);
      }

      return {
        ...col,
        minWidth: newMinWidth,
        flex: col.field === "tx_notes" ? 1.2 : col.flex,
      };
    }),
    [isMobile]
  );

  return (
    <Box sx={{ width: "auto", px: { xs: 0, sm: 3 }, py: 3 }}>
      <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: { xs: 1.5, sm: 2 } }}>
        <Grid item lg={4} md={6} sm={12} xs={12}>
          <BalanceCard onTopUpClick={() => setTopupFormModal({ open: true })} />
        </Grid>
      </Grid>
      <CardBase
        title="Transaction History"
        useGlassCard={true}
        nested={false}
      >
        <GlassDataGrid
          height={isMobile ? "70vh" : "70vh"}
          rows={rows || []}
          columns={columns}
          getRowHeight={() => "auto"}
          getEstimatedRowHeight={() => 72}
          paginationMode="server"
          rowCount={rowCount}
          page={page as any}
          onPageChange={(newPage: any) => setPage(newPage)}
          pageSize={pageSize as any}
          rowsPerPageOptions={[1000]}
          onPageSizeChange={(newSize: any) => setPageSize(newSize)}
          loading={isLoading || isFetching}
          slots={{
            loadingOverlay: LinearProgress,
            noRowsOverlay: NoResultsOverlay,
            toolbar: CustomToolbar,
          }}
          disableSelectionOnClick
          disableColumnFilter
          density="compact"
          getRowClassName={getRowClassName}
          rowHeight={isMobile ? 56 : 64}
          sx={{
            "& .MuiDataGrid-toolbarContainer": {
              padding: { xs: "12px 16px", sm: "16px 20px" },
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: isDark
                ? "rgba(255, 255, 255, 0.08)"
                : "rgba(255, 255, 255, 0.5)",
              backdropFilter: "blur(15px) saturate(180%)",
              WebkitBackdropFilter: "blur(15px) saturate(180%)",
              borderBottom: `2px solid ${isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(46, 125, 60, 0.3)"}`,
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
                fontSize: { xs: "0.7rem", sm: "0.875rem" },
                textTransform: "uppercase",
                letterSpacing: "0.8px",
                color: "#12903C",
              },
            },
            "& .MuiDataGrid-cell": {
              whiteSpace: "normal",
              lineHeight: 1.35,
              display: "block",
              paddingTop: 1,
              paddingBottom: 1,
            },
          }}
        />
      </CardBase>
      <TopupModalForm
        open={topupFormModal.open}
        onClose={() => setTopupFormModal({ open: false })}
      />
    </Box>
  );
};
export default FinancesPage;
